import { X } from 'lucide-react'
import PropTypes from 'prop-types'

import { AdasModal } from '@components/wrapper-components'
import { useIsReadOnly } from '@hooks/useIsReadOnly'

/** Common modal shell used across the Quote / Work Order flows.
 *
 *  Layout (matches the Edit Calibration mockup):
 *    ┌────────────────────────────────────┐
 *    │ Title                            X │   ← header (no border)
 *    │  ┌──────────────────────────────┐  │
 *    │  │  ...children (form + CTA)... │  │  ← parent card (rounded border)
 *    │  └──────────────────────────────┘  │
 *    └────────────────────────────────────┘
 *
 *  The parent card scrolls when the children exceed `maxHeight`. Header + X
 *  stay pinned at the top of the modal. Consumers control their own internal
 *  layout (form rows, button row, etc.) inside `children`.
 *
 *  Pass `bare` to render `children` directly without the inner bordered card —
 *  useful for content that already provides its own visual containers.
 *
 *  Read-only users: the body is wrapped in a disabled <fieldset>, same as FormModal,
 *  so the confirm/save controls inside are inert. The close button stays live. */
export const BrandedModal = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  width = 'w-[520px]',
  maxHeight = 'max-h-[90vh]',
  bare = false
}) => {
  const isReadOnly = useIsReadOnly()

  return (
    <AdasModal open={open} onClose={onClose} className={`flex ${width} max-w-[92vw] ${maxHeight} flex-col overflow-hidden bg-white p-5`}>
      <>
        {/* Header — title + close, no divider */}
        <div className='mb-4 flex items-start justify-between gap-3'>
          <div className='min-w-0'>
            <h2 className='text-[18px] font-bold text-[#0A1628]'>{title}</h2>
            {subtitle && <p className='mt-0.5 text-[13px] text-[#6B7280]'>{subtitle}</p>}
          </div>
          <button
            type='button'
            onClick={onClose}
            aria-label='Close'
            className='inline-flex h-8 w-8 flex-none items-center justify-center rounded-md border border-[#D1D5D8] bg-white text-[#475569] transition-colors hover:bg-[#F8FAFC]'
          >
            <X className='h-4 w-4' />
          </button>
        </div>
        {/* Parent card */}
        <fieldset disabled={isReadOnly} className='contents'>
          {bare ? (
            <div className='min-h-0 flex-1 overflow-y-auto'>{children}</div>
          ) : (
            <div className='min-h-0 flex-1 overflow-y-auto rounded-xl border border-[#D1D5D8] bg-white p-5'>{children}</div>
          )}
        </fieldset>
      </>
    </AdasModal>
  )
}

BrandedModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  children: PropTypes.node,
  width: PropTypes.string,
  maxHeight: PropTypes.string,
  bare: PropTypes.bool
}
